/* RESPONSIBLE TEAM: team-standalone */

import { all, hash } from 'rsvp';
import { taskFor } from 'ember-concurrency-ts';
import { dropTask, restartableTask } from 'ember-concurrency-decorators';
import { get, post } from 'embercom/lib/ajax';
import { type TaskGenerator } from 'ember-concurrency';
import type Store from '@ember-data/store';
import type SalesforcePlatform from 'embercom/objects/standalone/platforms/salesforce';
import type {
  SalesforceAdmin,
  SalesforceQueue,
  SalesforceSupportEmail,
} from 'embercom/objects/standalone/salesforce';
import {
  TicketField,
  type TicketFieldResponse,
  UserField,
  type UserFieldResponse,
} from 'embercom/objects/standalone/data-fields/common';
import { SalesforceDataFields } from 'embercom/objects/standalone/data-fields/salesforce';

export default class SalesforceDataLoader {
  platform: SalesforcePlatform;
  private store!: Store;

  constructor(platform: SalesforcePlatform) {
    this.platform = platform;
    this.store = platform.store;
  }

  get app() {
    return this.platform.finStandaloneService.app;
  }

  get isLoadingSalesforceConfig() {
    return taskFor(this.loadSalesforceConfig).isRunning;
  }

  get isLoadingSalesforceImportOnlyConfig() {
    return taskFor(this.loadSalesforceImportOnlyConfig).isRunning;
  }

  get isLoadingSalesforceSetupData() {
    return taskFor(this.loadSalesforceSetupData).isRunning;
  }

  get isLoadingSalesforceInAppMessagingConfig() {
    return taskFor(this.loadSalesforceInAppMessagingConfig).isRunning;
  }

  get isLoadingSalesforceLiveChatConfig() {
    return taskFor(this.loadSalesforceLiveChatConfig).isRunning;
  }

  get isLoadingSalesforceDataFields() {
    return taskFor(this.loadSalesforceDataFields).isRunning;
  }

  get isLoadingSalesforcePermissions() {
    return taskFor(this.checkSalesforcePermissions).isRunning;
  }

  get isLoadingSalesforcePermissionsHealthcheck() {
    return taskFor(this.loadSalesforcePermissionsHealthcheck).isRunning;
  }

  get isLoadingSalesforceCopilotInstallation() {
    return taskFor(this.checkSalesforceCopilotInstallationTask).isRunning;
  }

  async performLoads() {
    let promises: Promise<unknown>[] = [
      taskFor(this.loadSalesforceConfig).perform(),
      taskFor(this.loadSalesforceImportOnlyConfig).perform(),
      this.store.findAll('standalone/external-field-identifier'),
    ];

    await all(promises);
  }

  async performLazyLoads() {
    this.app.loadEmailDomains(false);
    taskFor(this.loadSalesforceSetupData).perform();
    taskFor(this.loadSalesforceInAppMessagingConfig).perform();
    taskFor(this.loadSalesforceLiveChatConfig).perform();
    taskFor(this.loadSalesforceDataFields).perform();
  }

  @dropTask *loadSalesforceConfig() {
    try {
      this.platform.salesforceConfig = yield this.store.findRecord(
        'standalone/salesforce-configuration',
        this.app.id,
      );

      if (!this.platform.salesforceConfig) {
        this.platform.salesforceConfig = this.store.createRecord(
          'standalone/salesforce-configuration',
          {
            id: this.app.id,
          },
        );
      }
    } catch (e) {
      console.error(e.message || e.jqXHR);
      if (e.jqXHR?.status !== 404) {
        throw e;
      }
    }
  }

  @dropTask *loadSalesforceImportOnlyConfig(): TaskGenerator<void> {
    // TODO Replace this with usage of Ember Data?
    let salesforceImportOnlyConfig = yield get(
      `/ember/standalone/salesforce_configurations/${this.app.id}/find_import_only_integration`,
      {
        app_id: this.app.id,
      },
    ).catch(() => ({}));

    if (salesforceImportOnlyConfig?.instance_url) {
      this.platform.salesforceImportOnlyConfig = {
        instanceUrl: salesforceImportOnlyConfig.instance_url,
        organizationId: salesforceImportOnlyConfig.organization_id,
        isSandbox: salesforceImportOnlyConfig.is_sandbox,
      };
    }
  }

  @dropTask *loadSalesforceSetupData(): TaskGenerator<void> {
    if (this.platform.salesforceConfig?.isPendingSetup) {
      this.platform.salesforceSetupData = {
        admins: [],
        queues: [],
        caseOrigins: [],
        supportEmails: [],
        permissionsHealthcheck: [],
        hasPermissionSet: undefined,
        hasPermissionSetAssignedToInstaller: undefined,
        copilot: {
          isInstalled: false,
          hasCustomSettingsFields: false,
          installedOnLightningPages: false,
          unableToDetectLightningPageInstallation: false,
          lightningWebSecurityEnabled: false,
        },
      };

      return;
    }

    // TODO Replace this with usage of Ember Data
    let promises = {
      salesforceAdmins: get(
        `/ember/standalone/salesforce_configurations/${this.app.id}/list_salesforce_users`,
        {
          app_id: this.app.id,
        },
      ).catch(() => []),
      salesforceQueues: get(
        `/ember/standalone/salesforce_configurations/${this.app.id}/list_salesforce_queues`,
        {
          app_id: this.app.id,
        },
      ).catch(() => []),
      salesforceCaseOrigins: get(
        `/ember/standalone/salesforce_configurations/${this.app.id}/list_salesforce_case_origins`,
        {
          app_id: this.app.id,
        },
      ).catch(() => []),
      selectedCaseOrigins: get(
        `/ember/standalone/salesforce_configurations/${this.app.id}/list_selected_case_origins`,
        {
          app_id: this.app.id,
        },
      ),
      salesforceSupportEmails: get(
        `/ember/standalone/salesforce_configurations/${this.app.id}/list_support_emails`,
        {
          app_id: this.app.id,
        },
      ).catch(() => []),
      checkPermissions: get(
        `/ember/standalone/salesforce_configurations/${this.app.id}/check_permissions`,
        {
          app_id: this.app.id,
        },
      ).catch(() => ({})),
      permissionsHealthcheck: get('/ember/standalone/healthcheck', {
        app_id: this.app.id,
      }).catch(() => []),
      checkSalesforceCopilotInstallation: this.checkSalesforceCopilotInstallation(),
    };

    let {
      salesforceAdmins,
      salesforceQueues,
      salesforceCaseOrigins,
      selectedCaseOrigins,
      salesforceSupportEmails,
      checkPermissions,
      permissionsHealthcheck,
      checkSalesforceCopilotInstallation,
    } = yield hash(promises);

    this.platform.salesforceSetupData = {
      admins: salesforceAdmins as Array<SalesforceAdmin>,
      queues: salesforceQueues as Array<SalesforceQueue>,
      caseOrigins: salesforceCaseOrigins.sort() as Array<string | { value: string; label: string }>,
      supportEmails: salesforceSupportEmails as Array<SalesforceSupportEmail>,
      permissionsHealthcheck,
      hasPermissionSet: checkPermissions.has_permission_set,
      hasPermissionSetAssignedToInstaller:
        checkPermissions.has_permission_set_assigned_to_installer,
      copilot: {
        isInstalled: checkSalesforceCopilotInstallation.has_copilot_installed,
        hasCustomSettingsFields: checkSalesforceCopilotInstallation.has_custom_settings_fields,
        installedOnLightningPages: checkSalesforceCopilotInstallation.installed_on_lightning_pages,
        unableToDetectLightningPageInstallation:
          checkSalesforceCopilotInstallation.unable_to_detect_lightning_page_installation,
        lightningWebSecurityEnabled: checkSalesforceCopilotInstallation.lws_enabled,
      },
    };

    this.platform.selectedSalesforceCaseOrigins = selectedCaseOrigins;
  }

  @dropTask *loadSalesforceInAppMessagingConfig(): TaskGenerator<void> {
    if (this.platform.salesforceConfig?.isPendingSetup) {
      return;
    }

    try {
      this.platform.salesforceInAppMessagingConfig = yield this.store.findRecord(
        'standalone/salesforce-in-app-messaging-configuration',
        this.app.id,
      );
    } catch (e) {
      console.error(e.message || e.jqXHR);
      if (e.jqXHR?.status !== 404) {
        throw e;
      }
    }
  }

  @dropTask *loadSalesforceSupportEmails(): TaskGenerator<void> {
    if (this.platform.salesforceConfig?.isPendingSetup) {
      return;
    }

    let supportEmails = yield get(
      `/ember/standalone/salesforce_configurations/${this.app.id}/list_support_emails`,
      {
        app_id: this.app.id,
      },
    ).catch(() => []) as Array<SalesforceSupportEmail>;

    this.platform.salesforceSetupData = {
      ...this.platform.salesforceSetupData,
      supportEmails,
    };
  }

  @dropTask *loadSalesforceLiveChatConfig(): TaskGenerator<void> {
    if (!this.app.canUseHandoffToSalesforceChat || this.platform.salesforceConfig?.isPendingSetup) {
      return;
    }

    try {
      this.platform.salesforceLiveChatConfig = yield this.store.findRecord(
        'standalone/salesforce-live-chat-configuration',
        this.app.id,
      );
    } catch (e) {
      console.error(e.message || e.jqXHR);
      if (e.jqXHR?.status !== 404) {
        throw e;
      }
    }
  }

  @dropTask *loadSalesforceDataFields(): TaskGenerator<void> {
    yield this.reloadSalesforceDataFields();
  }

  @dropTask *loadSalesforcePermissionsHealthcheck(): TaskGenerator<void> {
    if (this.platform.salesforceConfig?.isPendingSetup) {
      return;
    }

    let permissionsHealthcheck = yield get('/ember/standalone/healthcheck', {
      app_id: this.app.id,
    }).catch(() => []);

    this.platform.salesforceSetupData = {
      ...this.platform.salesforceSetupData,
      permissionsHealthcheck,
    };
  }

  async reloadSalesforceDataFields() {
    if (this.platform.salesforceConfig?.isPendingSetup) {
      this.platform.salesforceDataFields = new SalesforceDataFields({
        userFields: [],
        caseFields: [],
        cvdasForRequiredSalesforceCaseFields: [],
      });
      return;
    }

    // TODO Replace this with usage of Ember Data
    let dataFields = (await get(`/ember/standalone/salesforce_data/available_fields`, {
      app_id: this.app.id,
      admin_id: this.app.currentAdmin.id,
    })) as {
      user_fields: UserFieldResponse[];
      case_fields: TicketFieldResponse[];
      cvdas_for_required_salesforce_case_fields: {
        intercom_cvda: { id: string };
        salesforce_field_id: string;
      }[];
    };

    this.platform.salesforceDataFields = new SalesforceDataFields({
      userFields: dataFields.user_fields.map((field) => new UserField(field)),
      caseFields: dataFields.case_fields.map((field) => new TicketField(field)),
      cvdasForRequiredSalesforceCaseFields: dataFields.cvdas_for_required_salesforce_case_fields,
    });
  }

  @restartableTask *checkSalesforcePermissions(): TaskGenerator<void> {
    let { has_permission_set, has_permission_set_assigned_to_installer } = yield get(
      `/ember/standalone/salesforce_configurations/${this.app.id}/check_permissions`,
      {
        app_id: this.app.id,
      },
    ).catch(() => ({
      has_permission_set: this.platform.salesforceSetupData.hasPermissionSet,
      has_permission_set_assigned_to_installer:
        this.platform.salesforceSetupData.hasPermissionSetAssignedToInstaller,
    }));

    if (has_permission_set_assigned_to_installer) {
      // Reload the setup data as we've just assigned the permission set to the installer
      yield taskFor(this.loadSalesforceSetupData).perform();
    } else {
      // Otherwise, just update the setup data with the permission set status
      this.platform.salesforceSetupData = {
        ...this.platform.salesforceSetupData,
        hasPermissionSet: has_permission_set,
        hasPermissionSetAssignedToInstaller: has_permission_set_assigned_to_installer,
      };
    }
  }

  @restartableTask *hasRequiredSalesforceFinPermissionsTask(): TaskGenerator<void> {
    if (this.platform.salesforceConfig?.isPendingSetup) {
      return;
    }

    // TODO Replace this with usage of Ember Data
    let hasPermissions = yield get(
      `/ember/standalone/salesforce_configurations/${this.app.id}/has_required_fin_permissions`,
      {
        app_id: this.app.id,
      },
    );
    this.platform.hasPermissionPackageBeenInstalled = hasPermissions.has_fin_permissions;
    this.platform.hasRequiredSalesforceFinPermissions = hasPermissions.has_admin_permission_set;
  }

  @restartableTask *checkSalesforceCopilotInstallationTask(): TaskGenerator<void> {
    let {
      has_copilot_installed,
      has_custom_settings_fields,
      installed_on_lightning_pages,
      unable_to_detect_lightning_page_installation,
      lws_enabled,
    } = yield this.checkSalesforceCopilotInstallation();

    this.platform.salesforceSetupData = {
      ...this.platform.salesforceSetupData,
      copilot: {
        ...this.platform.salesforceSetupData.copilot,
        isInstalled: has_copilot_installed,
        hasCustomSettingsFields: has_custom_settings_fields,
        installedOnLightningPages: installed_on_lightning_pages,
        unableToDetectLightningPageInstallation: unable_to_detect_lightning_page_installation,
        lightningWebSecurityEnabled: lws_enabled,
      },
    };
  }

  @dropTask *createCopilotCustomSettingsFieldsTask() {
    yield post(
      `/ember/standalone/salesforce_configurations/${this.app.id}/create_copilot_custom_settings_fields`,
    );

    this.platform.salesforceSetupData = {
      ...this.platform.salesforceSetupData,
      copilot: {
        ...this.platform.salesforceSetupData.copilot,
        hasCustomSettingsFields: true,
      },
    };
  }

  private checkSalesforceCopilotInstallation(): Promise<{
    has_copilot_installed: boolean;
    has_custom_settings_fields: boolean;
    installed_on_lightning_pages: boolean;
    unable_to_detect_lightning_page_installation: boolean;
    lws_enabled: boolean;
  }> {
    return get(
      `/ember/standalone/salesforce_configurations/${this.app.id}/check_copilot_installation`,
      {
        app_id: this.app.id,
      },
    ).catch(() => ({
      has_copilot_installed: false,
      has_custom_settings_fields: false,
      installed_on_lightning_pages: false,
      unable_to_detect_lightning_page_installation: false,
      lws_enabled: false,
    }));
  }
}
